import { c as _c } from "react/compiler-runtime";
import { Badge, Box, Heading, HStack, Separator, Text, VStack } from '@chakra-ui/react';
import { useIsMobile } from '@src/components/sco/Advisors/hooks/useIsMobile';
import { NestedAppLayout, attachedNavPanelContainerProps, attachedPanelContainerProps } from '@src/components/sco/AppLayout';
import { AppPanelToggleButton } from '@src/components/sco/AppLayout/Panel/AppPanelToggleButton';
import { SidebarNestedNavList, SidebarNestedNavListSection } from '@src/components/sco/SidebarNestedNavList';
import { ReactNode, useMemo } from 'react';
import { IconType } from 'react-icons';
export type SubNavItem = {
  key: string;
  title: string;
  href: string;
  icon: IconType;
  /** Badge count; omitted or 0 renders no badge. */
  count?: number;
};

/**
 * Count rides on the ReactNode title — `SidebarNestedNavListSection.badgeProps`
 * is declared but never rendered (same pattern as Inbox in AdvisorsNav).
 */
const SubNavItemTitle = t0 => {
  const $ = _c(7);
  const {
    title,
    count
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <Text flex={1} lineClamp={1} minWidth={0}>{title}</Text>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== count) {
    t2 = !!count && <Badge flexShrink={0} variant="countGeneric">{count}</Badge>;
    $[2] = count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <HStack align="center" gap={2} minWidth={0} width="full">{t1}{t2}</HStack>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const NAV_PANEL_ID = 'advisors-subnav-nav';

/**
 * Second-column layout for Advisors product areas: a sub-navigation rail on the
 * left, section content on the right.
 *
 * Nested AppLayout (same attached-panel pattern as Request Chat / Request Builder)
 * so Opportunities, Sessions, and My Advisors share chrome with the rest of
 * the product instead of a one-off Flex split.
 */
export const AdvisorsSubNavLayout = t0 => {
  const $ = _c(46);
  const {
    railTitle,
    railDescription,
    items,
    activeKey,
    railFooter,
    title,
    description,
    action,
    compactHeader: t1,
    children
  } = t0;
  const compactHeader = t1 === undefined ? false : t1;
  const isMobile = useIsMobile();
  let t2;
  if ($[0] !== activeKey || $[1] !== items) {
    let t3;
    if ($[3] !== activeKey) {
      t3 = item => ({
        href: item.href,
        icon: item.icon,
        isActive: item.key === activeKey,
        title: item.count ? <SubNavItemTitle count={item.count} title={item.title} /> : item.title
      });
      $[3] = activeKey;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = items.map(t3);
    $[0] = activeKey;
    $[1] = items;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const sections = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      base: true,
      md: false
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = isMobile ? "xl" : "lg";
  let t5;
  if ($[6] !== railTitle || $[7] !== t4) {
    t5 = <Heading size={t4}>{railTitle}</Heading>;
    $[6] = railTitle;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== railDescription) {
    t6 = railDescription && <Text color="colorPalette.fg/60" fontSize="sm">{railDescription}</Text>;
    $[9] = railDescription;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t5 || $[12] !== t6) {
    t7 = <VStack align="stretch" gap={1} paddingX={3}>{t5}{t6}</VStack>;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== sections) {
    t8 = <SidebarNestedNavList sections={sections} />;
    $[14] = sections;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== railFooter) {
    t9 = railFooter ? <Box>{railFooter}</Box> : null;
    $[16] = railFooter;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = {
      id: NAV_PANEL_ID,
      defaultWidth: 280,
      minWidth: 220,
      maxWidth: 360,
      isResizable: true,
      resizeHandlePosition: "right",
      useDrawerBreakpoints: t3,
      containerProps: attachedNavPanelContainerProps,
      children: <VStack align="stretch" gap={4} height="full" overflowY="auto" paddingX={4} paddingY={4} width="full">{t7}{t8}{t9}</VStack>
    };
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      base: 4,
      md: 6,
      lg: 8
    };
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      base: "block",
      md: "none"
    };
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Box display={t12} paddingTop={1}><AppPanelToggleButton panelId={NAV_PANEL_ID} /></Box>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = compactHeader || isMobile ? "xl" : "3xl";
  let t15;
  if ($[25] !== t14 || $[26] !== title) {
    t15 = <Heading size={t14}>{title}</Heading>;
    $[25] = t14;
    $[26] = title;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== description || $[29] !== isMobile) {
    t16 = description && <Text color="colorPalette.fg/60" fontSize={isMobile ? "sm" : "md"} maxWidth="6xl">{description}</Text>;
    $[28] = description;
    $[29] = isMobile;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== compactHeader) {
    t17 = !compactHeader && <Separator marginY={4} width="full" />;
    $[31] = compactHeader;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t15 || $[34] !== t16 || $[35] !== t17) {
    t18 = <HStack align="start" flex={1} gap={2} minWidth={0}>{t13}<VStack align="start" gap={0.5} minWidth={0} width="full">{t15}{t16}{t17}</VStack></HStack>;
    $[33] = t15;
    $[34] = t16;
    $[35] = t17;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== action || $[38] !== t18) {
    t19 = <HStack align="end" gap={3} justify="space-between" wrap="wrap">{t18}{action}</HStack>;
    $[37] = action;
    $[38] = t18;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== children || $[41] !== t19) {
    t20 = {
      id: "advisors-subnav-main",
      containerProps: attachedPanelContainerProps,
      children: <Box flex={1} height="full" minHeight={0} minWidth={0} overflowY="auto" padding={t11} width="full"><VStack align="stretch" gap={4} marginX="auto" maxWidth="6xl" width="full">{t19}{children}</VStack></Box>
    };
    $[40] = children;
    $[41] = t19;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t10 || $[44] !== t20) {
    t21 = <NestedAppLayout id="advisors-subnav" panels={[t10, t20]} />;
    $[43] = t10;
    $[44] = t20;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  return t21;
};